import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  ActionBar,
  Badge,
  Balance,
  Button,
  Callout,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Combobox,
  EntryCell,
  EntryGrid,
  EntryRow,
  Field,
  GridAction,
  GridInput,
  GridSelect,
  Input,
  type ComboboxOption,
  KeyValue,
  Money,
  PageHeader,
  Select,
  StatTile,
  Textarea,
  Toolbar,
  absDecimal,
  cmpDecimal,
  formatDate,
  formatMoney,
  isNegativeDecimal,
  type EntryColumn,
} from '@biovet/ui';
import { ApiError, useResource, type VoucherDetail, type VoucherListRow } from '../lib/api.js';
import { PAGE, esc, letterhead, send } from '../lib/print.js';
import {
  BRANCHES,
  PARTIES,
  accountLabel,
  accountsOfKind,
  defaultAccountOfKind,
  company,
  companyTimezone,
  entryAccounts,
  findAccount,
  findParty,
  hasSubAccounts,
  subAccountsOf,
  type Account,
} from '../lib/reference-data.js';
import {
  VOUCHER_TYPES,
  type VoucherTypeCode,
  type VoucherTypeConfig,
} from '../lib/voucher-config.js';
import {
  amountFieldFor,
  draftFromLoaded,
  duplicateDraft,
  emptyDraft,
  emptyLine,
  parseAmount,
  postingAccountOf,
  summarise,
  toPayload,
  validate,
  type Currency,
  type FxContext,
  type Issue,
  type Role,
  type VoucherDraft,
  type VoucherLineDraft,
} from '../lib/voucher-model.js';
import {
  deleteVoucher,
  listVouchers,
  loadVoucher,
  peekVoucherNumber,
  saveVoucher,
  saveVoucherBatch,
  type SaveStatus,
} from '../lib/voucher-store.js';
import {
  downloadText,
  parseRows,
  readSpreadsheet,
  templateCsv,
  type BulkParse,
} from '../lib/voucher-bulk.js';
import type { BulkResult } from '../lib/api.js';

export function VoucherEntryScreen({
  type,
  role,
  currency,
  fxRate,
  baseCurrency,
  branchCode,
  accounts,
}: {
  type: VoucherTypeCode;
  role: Role;
  currency: Currency;
  fxRate: string;
  baseCurrency: Currency;
  branchCode: string;
  accounts: Account[];
}) {
  const config = VOUCHER_TYPES[type];
  const fx = useMemo<FxContext>(
    () => ({ currency, fxRate, baseCurrency }),
    [currency, fxRate, baseCurrency],
  );
  const foreign = currency !== baseCurrency;

  const accountOptions = useMemo(() => accountOptionsFor(accounts, config), [accounts, config]);

  const contraOptions = useMemo<ComboboxOption[]>(() => {
    const kind = config.contra?.pickable ? config.contra.kind : null;
    if (!kind || accounts.length === 0) return [];
    return accountsOfKind(kind).map((account) => ({
      value: account.code,
      label: `${account.code} · ${account.name}`,
      hint: account.fcyCurrency
        ? `${account.fcyCurrency} · ${formatMoney(account.balanceBase)}`
        : formatMoney(account.balanceBase),
    }));
  }, [accounts, config]);

  const newDraft = useCallback(
    (): VoucherDraft => ({
      ...emptyDraft(),
      contraAccountCode: config.contra ? defaultAccountOfKind(config.contra.kind) : '',
    }),
    [config],
  );

  const [draft, setDraft] = useState<VoucherDraft>(newDraft);
  const [busy, setBusy] = useState<Busy>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appending, setAppending] = useState(false);
  const [postedLines, setPostedLines] = useState<ReadonlySet<string>>(new Set());
  const [record, setRecord] = useState<VoucherDetail | null>(null);
  const [focusLine, setFocusLine] = useState<string | null>(null);
  const accountRefs = useRef(new Map<string, HTMLInputElement | null>());

  const [register, setRegister] = useState<VoucherListRow[]>([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [nextNumber, setNextNumber] = useState('…');

  const [headerOpen, setHeaderOpen] = useState(false);

  const [bulk, setBulk] = useState<BulkParse | null>(null);
  const [bulkFile, setBulkFile] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const summary = useMemo(() => summarise(draft, config, fx), [draft, config, fx]);
  const validation = useMemo(
    () => validate(draft, config, summary, role, fx),
    [draft, config, summary, role, fx],
  );

  const balanceCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const line of draft.lines) {
      const code = postingAccountOf(line);
      if (code) codes.add(code);
    }
    if (draft.contraAccountCode) codes.add(draft.contraAccountCode);
    return [...codes].sort();
  }, [draft.lines, draft.contraAccountCode]);

  const balances = useResource<{ balances: Record<string, { balanceBase: string }> }>(
    balanceCodes.length > 0 ? '/account-balances' : null,
    { codes: balanceCodes.join(',') },
  );
  const balanceOf = (code: string): string | undefined =>
    balances.data?.balances[code]?.balanceBase;

  const posted = draft.status === 'POSTED';
  const amountField = amountFieldFor(config);
  const headerLocked = posted;
  const lineLocked = (line: VoucherLineDraft) => posted && postedLines.has(line.id);
  const canAddLines = !posted || appending;

  useEffect(() => {
    if (!focusLine) return;
    accountRefs.current.get(focusLine)?.focus();
    setFocusLine(null);
  }, [focusLine]);

  useEffect(() => {
    if (!config.contra || accounts.length === 0) return;
    const fallback = defaultAccountOfKind(config.contra.kind);
    if (!fallback) return;
    setDraft((d) => (d.contraAccountCode ? d : { ...d, contraAccountCode: fallback }));
  }, [accounts, config]);

  const HEADER_FIELDS = config.contra?.pickable
    ? ['voucherDate', 'fxRate', 'contraAccountCode']
    : ['voucherDate', 'fxRate'];
  const headerErrorCount = validation.errors.filter(
    (issue) => issue.field && HEADER_FIELDS.includes(issue.field),
  ).length;
  const headerHasError = headerErrorCount > 0;

  useEffect(() => {
    if (headerHasError) setHeaderOpen(true);
  }, [headerHasError]);


  const refreshRegister = useCallback(async () => {
    try {
      setRegister(await listVouchers(type));
    } catch {
      setRegister([]);
    }
  }, [type]);

  const refreshNumber = useCallback(async () => {
    try {
      setNextNumber(await peekVoucherNumber(type));
    } catch {
      setNextNumber('—');
    }
  }, [type]);

  useEffect(() => {
    void refreshRegister();
    void refreshNumber();
  }, [refreshRegister, refreshNumber]);

  const position = draft.voucherNo
    ? register.findIndex((row) => row.voucherNo === draft.voucherNo)
    : -1;

  const dirty = draft.voucherNo === null && summary.filledLines > 0;

  const discardable = () =>
    !dirty || window.confirm('This voucher has not been saved. Discard it and load another?');


  const openVoucher = async (voucherNo: string) => {
    setBusy('load');
    setError(null);
    setFlash(null);
    try {
      const detail = await loadVoucher(type, voucherNo);
      const loaded = draftFromLoaded(detail, config);
      setDraft(loaded);
      setRecord(detail);
      setPostedLines(
        detail.status === 'DRAFT' || detail.status === 'PENDING_APPROVAL'
          ? new Set()
          : new Set(loaded.lines.map((line) => line.id)),
      );
      setAppending(false);
      if (detail.reversedByNo) {
        setFlash(`${detail.voucherNo} was reversed by ${detail.reversedByNo}.`);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  const goTo = (index: number) => {
    const row = register[index];
    if (!row || !discardable()) return;
    void openVoucher(row.voucherNo);
  };

  const first = register.length - 1;
  const last = 0;
  const atStart = register.length === 0 || position === first;
  const atEnd = register.length === 0 || position === last;
  const noRecords = register.length === 0;

  const searchMatches = search.trim()
    ? register.filter((row) => matchesNumber(row.voucherNo, search))
    : [];

  const runSearch = () => {
    const term = search.trim();
    if (!term) return;
    if (searchMatches.length === 0) {
      setError(`No ${config.label} numbered “${term}”.`);
      return;
    }
    setError(null);
    setRegisterOpen(true);
    const only = searchMatches[0];
    if (searchMatches.length === 1 && only && discardable()) void openVoucher(only.voucherNo);
  };

  const editVoucher = () => {
    setHeaderOpen(true);
    if (posted && !appending) {
      setAppending(true);
      addLine();
    }
  };


  const patch = (partial: Partial<VoucherDraft>) => setDraft((d) => ({ ...d, ...partial }));

  const patchLine = (id: string, partial: Partial<VoucherLineDraft>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.id === id ? { ...line, ...partial } : line)),
    }));

  const setAmount = (id: string, value: string) =>
    patchLine(id, amountField === 'debit' ? { debit: value } : { credit: value });

  const addLine = (partial: Partial<VoucherLineDraft> = {}) => {
    const line = { ...emptyLine(), ...partial };
    setDraft((d) => ({ ...d, lines: [...d.lines, line] }));
    setFocusLine(line.id);
    return line;
  };

  const removeLine = (id: string) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((line) => line.id !== id) : d.lines,
    }));

  const addBalancingLine = () => {
    const difference = summary.differenceTxn;
    if (cmpDecimal(difference, '0') === 0) return;
    const magnitude = absDecimal(difference);
    addLine(isNegativeDecimal(difference) ? { debit: magnitude } : { credit: magnitude });
  };

  const reset = () => {
    if (!discardable()) return;
    setDraft(newDraft());
    setRecord(null);
    setAppending(false);
    setPostedLines(new Set());
    setFlash(null);
    setError(null);
    setRegisterOpen(false);
  };

  const duplicate = () => {
    setDraft(duplicateDraft(draft));
    setRecord(null);
    setAppending(false);
    setPostedLines(new Set());
    setFlash(null);
    setError(null);
  };

  const submitVoucher = async (status: SaveStatus) => {
    setBusy(status === 'POSTED' ? 'post' : status === 'DRAFT' ? 'draft' : 'submit');
    setError(null);
    setFlash(null);
    try {
      const saved = await saveVoucher(toPayload(type, draft, summary, fx, branchCode), status);

      setDraft((d) => ({
        ...d,
        voucherNo: saved.voucherNo,
        status:
          saved.status === 'POSTED'
            ? 'POSTED'
            : saved.status === 'PENDING_APPROVAL'
              ? 'PENDING_APPROVAL'
              : 'DRAFT',
        approvalState: saved.status === 'PENDING_APPROVAL' ? 'PENDING' : 'NOT_REQUIRED',
      }));

      if (saved.status === 'POSTED') {
        setPostedLines(
          new Set(
            summary.entered
              .filter((entry) => entry.debit !== null || entry.credit !== null)
              .map((entry) => entry.line.id),
          ),
        );
      }

      setAppending(false);
      setFlash(
        status === 'DRAFT'
          ? `Saved as draft ${saved.voucherNo}. Nothing has reached the ledger.`
          : status === 'PENDING_APPROVAL'
            ? `${saved.voucherNo} submitted for approval — it sits with a checker until approved.`
            : `${saved.voucherNo} posted. ${saved.lineCount} lines, ${formatMoney(saved.totalDebitBase)} each side.`,
      );

      await Promise.all([refreshRegister(), refreshNumber()]);
      if (saved.status === 'POSTED') balances.reload();

      try {
        setRecord(await loadVoucher(type, saved.voucherNo));
      } catch {
        setRecord(null);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };


  const shortcuts = useRef<(event: globalThis.KeyboardEvent) => void>(() => {});

  shortcuts.current = (event) => {
    if (busy !== null) return;

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    const typing = tag === 'TEXTAREA';
    const activating = tag === 'BUTTON' || tag === 'A' || tag === 'SELECT';
    const expanded = target?.getAttribute('aria-expanded');

    if (event.altKey || event.ctrlKey || event.metaKey) {
      if (event.defaultPrevented) return;
    }

    if (event.altKey && (event.key === 'a' || event.key === 'A')) {
      if (posted && !appending) {
        if (validation.period && validation.period.status !== 'OPEN') return;
        event.preventDefault();
        setAppending(true);
        addLine();
        return;
      }
      if (!canAddLines) return;
      event.preventDefault();
      addLine();
      return;
    }

    if ((event.ctrlKey || event.metaKey) && (event.key === 'd' || event.key === 'D')) {
      if (!validation.canSaveDraft) return;
      event.preventDefault();
      void submitVoucher('DRAFT');
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      if (typing || activating) return;
      if (expanded === 'true') return;
      if (event.defaultPrevented && expanded === null) return;
      if (!validation.canPost) return;
      event.preventDefault();
      void submitVoucher('POSTED');
    }
  };

  useEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => shortcuts.current(event);
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, []);

  const removeVoucher = async () => {
    if (!draft.voucherNo) return;
    const isPosted = draft.status === 'POSTED';

    const confirmed = window.confirm(
      isPosted
        ? `${draft.voucherNo} is posted, so it cannot be deleted — the ledger is append-only.\n\nA reversal voucher will be posted instead: the same lines with the sides swapped, dated today. Both documents stay in the ledger and the account balances net to nothing.\n\nContinue?`
        : `Delete draft ${draft.voucherNo}?\n\nIt has not reached the ledger, so nothing is reversed. Its number stays consumed — the series is gapless by design.`,
    );
    if (!confirmed) return;

    let reason: string | undefined;
    if (isPosted) {
      const entered = window.prompt('Reason for the reversal (recorded on the voucher):', '');
      if (entered === null) return;
      reason = entered.trim() || undefined;
    }

    setBusy('delete');
    setError(null);
    setFlash(null);
    try {
      const result = await deleteVoucher(type, draft.voucherNo, reason);
      setFlash(result.message);

      await Promise.all([refreshRegister(), refreshNumber()]);

      if (result.action === 'DELETED') {
        setDraft(newDraft());
        setRecord(null);
        setPostedLines(new Set());
      } else {
        await openVoucher(draft.voucherNo);
        setFlash(result.message);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };


  const onFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setBusy('bulk');
    setError(null);
    setBulkResult(null);
    try {
      const rows = await readSpreadsheet(file);
      setBulk(parseRows(rows, type, config, { currency, fxRate, baseCurrency, branchCode, role }));
      setBulkFile(file.name);
    } catch (cause) {
      setBulk(null);
      setBulkFile('');
      setError(messageOf(cause));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const uploadBulk = async (status: SaveStatus) => {
    if (!bulk) return;
    const ready = bulk.vouchers.filter((v) =>
      status === 'POSTED' ? v.validation.canPost : v.validation.canSaveDraft,
    );
    if (ready.length === 0) return;

    setBusy('bulk');
    setError(null);
    try {
      const result = await saveVoucherBatch(
        ready.map((v) => v.payload),
        status,
      );
      setBulkResult(result);
      await Promise.all([refreshRegister(), refreshNumber()]);
      if (result.failed === 0) {
        setBulk(null);
        setBulkFile('');
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  const bulkPostable = bulk?.vouchers.filter((v) => v.validation.canPost).length ?? 0;
  const bulkBlocked = (bulk?.vouchers.length ?? 0) - bulkPostable;


  const columns = lineColumns(config, currency, baseCurrency);
  const branchName = BRANCHES.find((b) => b.code === branchCode)?.name ?? branchCode;
  const periodBlocked = validation.period && validation.period.status !== 'OPEN';
  const shownNumber = draft.voucherNo ?? nextNumber;

  return (
    <>
      <PageHeader
        breadcrumb={`Finance & Accounts / ${config.label}`}
        title={`${config.label} · ${shownNumber}`}
        description={config.description}
        meta={
          <>
            <Badge status={posted ? 'POSTED' : draft.status === 'NEW' ? 'DRAFT' : draft.status} />
            <Chip>{branchName}</Chip>
            <Chip>{foreign ? `${currency} @ ${fxRate}` : currency}</Chip>
            {periodBlocked && (
              <Badge tone="warn">
                {validation.period?.name} · {validation.period?.status.toLowerCase()}
              </Badge>
            )}
            {validation.requiresApproval && <Badge tone="warn">Needs a checker</Badge>}
          </>
        }
      />

      {error && (
        <div data-noprint="1" className="pb-3.5">
          <Callout tone="danger" title="The ledger refused this">
            {error}
          </Callout>
        </div>
      )}

      {flash && (
        <div data-noprint="1" className="pb-3.5">
          <Callout tone={posted ? 'success' : 'info'} title={flash}>
            {posted && (
              <span>
                A posted voucher is append-only: its lines cannot be edited or deleted, and a
                correction is a reversal voucher. Appending a line while the period is open is
                allowed — business flow §3.1.
              </span>
            )}
          </Callout>
        </div>
      )}

      {}
      <ActionBar placement="top">
        <span className="mr-auto text-[11.5px] text-[var(--ink-3)]">
          {validation.canPost
            ? 'Ready to post.'
            : `Cannot post — ${validation.postBlockers.join('; ')}.`}
        </span>

        <Button
          variant="secondary"
          loading={busy === 'draft'}
          disabled={!validation.canSaveDraft || busy !== null}
          onClick={() => void submitVoucher('DRAFT')}
          title="Ctrl-D"
        >
          Save draft
        </Button>

        {validation.requiresApproval && (
          <Button
            variant="secondary"
            loading={busy === 'submit'}
            disabled={!validation.canSubmit || busy !== null}
            onClick={() => void submitVoucher('PENDING_APPROVAL')}
          >
            Submit for approval
          </Button>
        )}

        <Button
          variant="primary"
          loading={busy === 'post'}
          disabled={!validation.canPost || busy !== null}
          onClick={() => void submitVoucher('POSTED')}
          title={validation.canPost ? 'Enter' : validation.postBlockers.join('; ')}
        >
          {posted && appending ? 'Post added lines' : 'Post voucher'}
        </Button>
      </ActionBar>

      <div className="grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_336px]">
        <div className="flex min-w-0 flex-col gap-3.5">
          {}
          <Card flush data-noprint="1">
            <Toolbar>
              <Button size="sm" variant="secondary" onClick={reset} disabled={busy !== null}>
                New
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={editVoucher}
                disabled={!draft.voucherNo || busy !== null || Boolean(posted && periodBlocked)}
                title={
                  !draft.voucherNo
                    ? 'Nothing loaded to edit'
                    : posted
                      ? 'Posted vouchers are append-only — this adds a line'
                      : 'Open the header and edit this draft'
                }
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void removeVoucher()}
                loading={busy === 'delete'}
                disabled={!draft.voucherNo || busy !== null}
                title={
                  !draft.voucherNo
                    ? 'Save the voucher before it can be deleted'
                    : draft.status === 'POSTED'
                      ? 'Posted — this will post a reversal voucher'
                      : 'Delete this draft'
                }
              >
                {draft.status === 'POSTED' ? 'Delete (reverses)' : 'Delete'}
              </Button>
              <Button
                size="sm"
                variant={registerOpen ? 'primary' : 'ghost'}
                onClick={() => setRegisterOpen((open) => !open)}
                disabled={busy !== null}
                aria-expanded={registerOpen}
                aria-controls="voucher-register"
                trailingIcon={<Chevron open={registerOpen} />}
              >
                View
              </Button>
              {}
              <Button
                size="sm"
                variant="ghost"
                disabled={!posted}
                title={posted ? 'Print this voucher' : 'Only a posted voucher can be printed'}
                onClick={() =>
                  printVoucher({
                    config,
                    draft,
                    summary,
                    record,
                    currency,
                    baseCurrency,
                    fxRate,
                  })
                }
              >
                Print
              </Button>

              <span className="mx-1 h-4 w-px bg-[var(--line-2)]" aria-hidden="true" />

              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(first)}
                disabled={noRecords || atStart || busy !== null}
                title="Oldest voucher of this type"
              >
                First
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(position === -1 ? last : position + 1)}
                disabled={noRecords || atStart || busy !== null}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(position === -1 ? last : position - 1)}
                disabled={noRecords || (position !== -1 && atEnd) || busy !== null}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(last)}
                disabled={noRecords || (position !== -1 && atEnd) || busy !== null}
                title="Most recent voucher of this type"
              >
                Last
              </Button>

              <div className="flex-1" />

              <Input
                size="sm"
                className="w-[160px]"
                placeholder={`Find ${type}-…`}
                aria-label="Search by voucher number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                leading={
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    aria-hidden="true"
                  >
                    <circle cx="6" cy="6" r="4.2" />
                    <path d="M9.2 9.2L12.5 12.5" strokeLinecap="round" />
                  </svg>
                }
              />
              <span className="bb-num shrink-0 text-[11.5px] text-[var(--ink-3)]">
                {noRecords
                  ? 'None yet'
                  : position === -1
                    ? `New · ${register.length}`
                    : `${register.length - position} of ${register.length}`}
              </span>
            </Toolbar>

            {registerOpen && (
              <div
                id="voucher-register"
                className="max-h-[280px] overflow-y-auto border-t border-[var(--line-2)]"
              >
                {(() => {
                  const shown = search.trim() ? searchMatches : register;
                  if (shown.length === 0) {
                    return (
                      <div className="px-[var(--pad)] py-3">
                        <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                          {search.trim()
                            ? `No ${config.label} matches “${search.trim()}”.`
                            : `Nothing posted or drafted for ${config.label} yet.`}
                        </p>
                      </div>
                    );
                  }
                  return shown.map((row) => (
                    <button
                      key={row.voucherNo}
                      type="button"
                      onClick={() => {
                        if (discardable()) void openVoucher(row.voucherNo);
                      }}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 text-left last:border-b-0 hover:bg-[var(--line-2)]/40 ${
                        row.voucherNo === draft.voucherNo ? 'bg-[var(--accent-soft)]' : ''
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="bb-mono block text-[12px] text-[var(--ink)]">
                          {row.voucherNo}
                        </span>
                        <span className="block truncate text-[11px] text-[var(--ink-3)]">
                          {formatDate(row.voucherDate)} · {row.lineCount} lines
                          {row.partyName ? ` · ${row.partyName}` : ''}
                          {row.narration ? ` · ${row.narration}` : ''}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <Money value={row.totalBase} className="text-[12px]" />
                        <Badge status={row.status} />
                      </span>
                    </button>
                  ));
                })()}
              </div>
            )}
          </Card>

          {}
          <Card flush>
            <CardHeader
              title="Voucher header"
              subtitle={
                headerOpen
                  ? 'Date and narration. Party belongs on the line that settles against it; currency, rate and branch come from the top bar, the cash or bank account is fixed by the voucher type, and the period is derived from the date.'
                  : headerSummary(draft, config, branchName)
              }
              actions={
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setHeaderOpen((open) => !open)}
                  aria-expanded={headerOpen}
                  aria-controls="voucher-header-body"
                >
                  {headerOpen ? 'Hide' : headerHasError ? 'Fix header' : 'Edit header'}
                  {!headerOpen && headerHasError && <Badge tone="bad">{headerErrorCount}</Badge>}
                </Button>
              }
            />
            {}
            {headerOpen && (
              <CardBody
                id="voucher-header-body"
                className="grid grid-cols-2 gap-x-4 gap-y-3.5 lg:grid-cols-4"
              >
                <Field label="Voucher no." hint="Gapless series, per type and fiscal year">
                  <Input
                    readOnly
                    value={shownNumber}
                    className="bb-mono"
                    aria-label="Voucher number"
                  />
                </Field>

                <Field
                  label="Voucher date"
                  required
                  htmlFor="voucher-date"
                  error={fieldError(validation.issues, 'voucherDate')}
                >
                  <Input
                    id="voucher-date"
                    type="date"
                    value={draft.voucherDate}
                    disabled={headerLocked}
                    invalid={Boolean(fieldError(validation.issues, 'voucherDate'))}
                    onChange={(e) => patch({ voucherDate: e.target.value })}
                  />
                </Field>

                {}
                {config.contra?.pickable && (
                  <Field
                    label={config.contra.label}
                    required
                    hint={`The ${config.contra.kind.toLowerCase()} account this voucher's ${config.contra.side === 'Cr' ? 'credit' : 'debit'} lands on`}
                    error={fieldError(validation.issues, 'contraAccountCode')}
                  >
                    <Combobox
                      value={draft.contraAccountCode}
                      disabled={headerLocked}
                      invalid={Boolean(fieldError(validation.issues, 'contraAccountCode'))}
                      options={contraOptions}
                      placeholder="Search code or name…"
                      emptyText={`No ${config.contra.kind.toLowerCase()} account in the chart`}
                      onChange={(code) => patch({ contraAccountCode: code })}
                      aria-label={config.contra.label}
                    />
                  </Field>
                )}

                <Field
                  label="Narration"
                  className="col-span-2 lg:col-span-4"
                  hint={
                    config.contra?.kind === 'BANK'
                      ? 'Heads the printed voucher. Put the cheque or transfer reference here — the General Ledger shows each line’s own narration, not this'
                      : 'Heads the printed voucher. The General Ledger shows each line’s own narration, not this'
                  }
                >
                  <Textarea
                    rows={2}
                    value={draft.narration}
                    disabled={headerLocked}
                    placeholder={config.narrationPlaceholder}
                    onChange={(e) => patch({ narration: e.target.value })}
                    aria-label="Narration"
                  />
                </Field>
              </CardBody>
            )}
          </Card>

          <Card flush>
            <CardHeader
              title={config.shape === 'JOURNAL' ? 'Journal lines' : `${config.amountLabel} lines`}
              subtitle={config.lineHelp}
            />

            <Toolbar>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => addLine()}
                disabled={!canAddLines}
              >
                Add line
              </Button>
              {config.shape === 'JOURNAL' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={addBalancingLine}
                  disabled={!canAddLines || cmpDecimal(summary.differenceTxn, '0') === 0}
                >
                  Add balancing line
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={duplicate} disabled={busy !== null}>
                Duplicate
              </Button>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                Alt-A adds a line · Ctrl-D saves a draft · Enter posts
              </span>
              <div className="flex-1" />
              {summary.balanced ? (
                <Badge tone="ok" dot>
                  Balanced
                </Badge>
              ) : (
                <Badge tone="warn" dot>
                  {formatMoney(absDecimal(summary.differenceBase))} out
                </Badge>
              )}
            </Toolbar>

            <EntryGrid
              aria-label={`${config.label} lines`}
              columns={columns}
              footer={
                <>
                  <EntryRow tone="generated">
                    <EntryCell padded className="text-[var(--ink-3)]">
                      —
                    </EntryCell>
                    <EntryCell padded className="font-semibold">
                      {totalRowLabel(config, draft, currency)}
                    </EntryCell>
                    {}
                    <EntryCell />
                    <EntryCell />
                    <EntryCell padded numeric>
                      <LineBalance
                        accountCode={config.shape === 'CONTRA' ? draft.contraAccountCode : ''}
                        balanceBase={balanceOf(draft.contraAccountCode)}
                        baseCurrency={baseCurrency}
                      />
                    </EntryCell>
                    {config.shape === 'JOURNAL' ? (
                      <>
                        <EntryCell padded numeric className="font-semibold">
                          <Money value={summary.totalDebitTxn} currency={currency} dashOnZero />
                        </EntryCell>
                        <EntryCell padded numeric className="font-semibold">
                          <Money value={summary.totalCreditTxn} currency={currency} dashOnZero />
                        </EntryCell>
                      </>
                    ) : (
                      <EntryCell padded numeric className="font-semibold">
                        <Money value={summary.totalDebitTxn} currency={currency} dashOnZero />
                      </EntryCell>
                    )}
                    <EntryCell noPrint />
                  </EntryRow>

                  {foreign && (
                    <EntryRow tone="generated">
                      <EntryCell padded className="text-[var(--ink-3)]">
                        —
                      </EntryCell>
                      <EntryCell padded className="text-[var(--ink-2)]">
                        Base equivalent · {baseCurrency} at {fxRate}
                      </EntryCell>
                      <EntryCell />
                      <EntryCell />
                      <EntryCell />
                      {config.shape === 'JOURNAL' ? (
                        <>
                          <EntryCell padded numeric>
                            <Money value={summary.totalDebitBase} dashOnZero />
                          </EntryCell>
                          <EntryCell padded numeric>
                            <Money value={summary.totalCreditBase} dashOnZero />
                          </EntryCell>
                        </>
                      ) : (
                        <EntryCell padded numeric>
                          <Money value={summary.totalDebitBase} dashOnZero />
                        </EntryCell>
                      )}
                      <EntryCell noPrint />
                    </EntryRow>
                  )}
                </>
              }
            >
              {draft.lines.map((line, index) => {
                const lineIssues = validation.issues.filter((i) => i.lineId === line.id);
                const lineError = lineIssues.some((i) => i.severity === 'error');
                const last = index === draft.lines.length - 1;
                const rowLocked = lineLocked(line);

                return (
                  <EntryRow key={line.id} tone={lineError ? 'invalid' : 'default'}>
                    <EntryCell padded className="text-[11.5px] text-[var(--ink-3)]">
                      {index + 1}
                    </EntryCell>

                    <EntryCell>
                      <Combobox
                        ref={(el) => {
                          accountRefs.current.set(line.id, el);
                        }}
                        value={line.accountCode}
                        disabled={rowLocked}
                        invalid={lineError && !line.accountCode}
                        options={accountOptions}
                        placeholder="Search code or name…"
                        emptyText="No account matches"
                        onChange={(code) =>
                          patchLine(line.id, {
                            accountCode: code,
                            subAccountCode: '',
                            partyCode: '',
                          })
                        }
                        aria-label={`Line ${index + 1} account`}
                      />
                    </EntryCell>

                    <EntryCell>
                      {}
                      {hasSubAccounts(line.accountCode) ? (
                        <Combobox
                          value={line.subAccountCode}
                          disabled={rowLocked}
                          invalid={lineError && !line.subAccountCode}
                          options={subAccountOptionsFor(line.accountCode)}
                          caption={`Sub-accounts of ${accountLabel(line.accountCode)}`}
                          placeholder="Required — search…"
                          emptyText="No sub-account matches"
                          onChange={(code) => patchLine(line.id, { subAccountCode: code })}
                          aria-label={`Line ${index + 1} sub-account`}
                        />
                      ) : keepsSubLedger(line.accountCode) ? (
                        <Combobox
                          value={line.partyCode}
                          disabled={rowLocked}
                          invalid={lineError && !line.partyCode}
                          options={PARTY_OPTIONS}
                          placeholder="Required — search…"
                          emptyText="No party matches"
                          onChange={(code) => patchLine(line.id, { partyCode: code })}
                          aria-label={`Line ${index + 1} sub-account`}
                        />
                      ) : (
                        <span className="px-2 text-[11.5px] text-[var(--ink-3)]">
                          {line.accountCode ? 'No sub-ledger' : '—'}
                        </span>
                      )}
                    </EntryCell>

                    <EntryCell>
                      <GridInput
                        value={line.narration}
                        disabled={rowLocked}
                        placeholder={index === 0 ? config.narrationPlaceholder : 'Line narration'}
                        onChange={(e) => patchLine(line.id, { narration: e.target.value })}
                        aria-label={`Line ${index + 1} narration`}
                      />
                    </EntryCell>

                    {}
                    <EntryCell padded numeric>
                      <LineBalance
                        accountCode={postingAccountOf(line)}
                        balanceBase={balanceOf(postingAccountOf(line))}
                        baseCurrency={baseCurrency}
                      />
                    </EntryCell>

                    {config.shape === 'JOURNAL' ? (
                      <>
                        <EntryCell>
                          <GridInput
                            numeric
                            value={line.debit}
                            disabled={rowLocked || parseAmount(line.credit).value !== null}
                            invalid={parseAmount(line.debit).invalid}
                            placeholder="0.00"
                            onChange={(e) => patchLine(line.id, { debit: e.target.value })}
                            aria-label={`Line ${index + 1} debit`}
                          />
                        </EntryCell>
                        <EntryCell>
                          <GridInput
                            numeric
                            value={line.credit}
                            disabled={rowLocked || parseAmount(line.debit).value !== null}
                            invalid={parseAmount(line.credit).invalid}
                            placeholder="0.00"
                            onChange={(e) => patchLine(line.id, { credit: e.target.value })}
                            aria-label={`Line ${index + 1} credit`}
                          />
                        </EntryCell>
                      </>
                    ) : (
                      <EntryCell>
                        <GridInput
                          numeric
                          value={line[amountField]}
                          disabled={rowLocked}
                          invalid={parseAmount(line[amountField]).invalid}
                          placeholder="0.00"
                          onChange={(e) => setAmount(line.id, e.target.value)}
                          aria-label={`Line ${index + 1} amount`}
                        />
                      </EntryCell>
                    )}

                    <EntryCell noPrint>
                      <GridAction
                        label={`Remove line ${index + 1}`}
                        disabled={rowLocked || draft.lines.length === 1}
                        onClick={() => removeLine(line.id)}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M1 1l8 8M9 1L1 9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </GridAction>
                    </EntryCell>
                  </EntryRow>
                );
              })}
            </EntryGrid>

            <CardFooter>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                {summary.filledLines} of {draft.lines.length} rows carry an amount ·{' '}
                {summary.glLines.length} GL lines will be written
              </span>
              <span className="bb-num text-[11.5px] text-[var(--ink-3)]">
                {currency} · rate {fxRate}
              </span>
            </CardFooter>
          </Card>

          <BulkPanel
            config={config}
            type={type}
            bulk={bulk}
            bulkFile={bulkFile}
            result={bulkResult}
            busy={busy === 'bulk'}
            disabled={busy !== null}
            fileInput={fileInput}
            postable={bulkPostable}
            blocked={bulkBlocked}
            onFile={(file) => void onFileChosen(file)}
            onUpload={(status) => void uploadBulk(status)}
            onClear={() => {
              setBulk(null);
              setBulkFile('');
              setBulkResult(null);
            }}
          />
        </div>

        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader title="Posting summary" subtitle={`Base currency — ${baseCurrency}`} />
            <CardBody className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <StatTile
                  label="Debit"
                  value={formatMoney(summary.totalDebitBase, { decimals: 0 })}
                  hint={`${summary.glLines.filter((l) => l.side === 'Dr').length} lines`}
                />
                <StatTile
                  label="Credit"
                  value={formatMoney(summary.totalCreditBase, { decimals: 0 })}
                  hint={`${summary.glLines.filter((l) => l.side === 'Cr').length} lines`}
                />
              </div>

              <div
                className={`flex items-center justify-between rounded-[var(--radius)] border px-3 py-2 ${
                  summary.balanced
                    ? 'border-[var(--credit)]/30 bg-[var(--accent-soft)]'
                    : 'border-[var(--warn)]/30 bg-[var(--warn-soft)]'
                }`}
              >
                <span className="text-[12px] font-medium text-[var(--ink-2)]">Difference</span>
                <Money value={summary.differenceBase} colorBySign={!summary.balanced} dashOnZero />
              </div>

              {}
              <KeyValue
                items={[
                  { label: 'Voucher', value: shownNumber, mono: true },
                  { label: 'Date', value: formatDate(draft.voucherDate) },
                  {
                    label: 'Period',
                    value: validation.period
                      ? `${validation.period.name} · ${validation.period.status.toLowerCase()}`
                      : '—',
                  },
                  { label: 'Branch', value: branchName },
                  { label: 'Created by', value: record?.createdByName ?? '—' },
                  { label: 'Last update on', value: stamp(record?.updatedAt) },
                  { label: 'Update by', value: record?.updatedByName ?? '—' },
                  {
                    label: 'Approval',
                    value: `over ${formatMoney(config.approvalThreshold, { decimals: 0 })} needs a checker`,
                  },
                ]}
              />
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader
              title="Validation"
              subtitle={
                validation.errors.length === 0
                  ? 'Every rule the database will apply, applied here first'
                  : `${validation.errors.length} to fix before posting`
              }
              actions={
                validation.errors.length === 0 ? (
                  <Badge tone="ok" dot>
                    Clear
                  </Badge>
                ) : (
                  <Badge tone="bad">{validation.errors.length}</Badge>
                )
              }
            />
            <CardBody className="flex flex-col gap-2">
              {validation.issues.length === 0 ? (
                <p className="m-0 text-[12.5px] text-[var(--ink-2)]">
                  Balanced, in an open period, and every line resolves. Posting is the only thing
                  left.
                </p>
              ) : (
                validation.issues.map((issue, i) => (
                  <div key={i} className="flex gap-2">
                    <span
                      aria-hidden="true"
                      className={`mt-[5px] size-1.5 shrink-0 rounded-full ${
                        issue.severity === 'error' ? 'bg-[var(--debit)]' : 'bg-[var(--warn)]'
                      }`}
                    />
                    <span className="text-[12px] text-[var(--ink-2)]">
                      {issue.lineId && (
                        <span className="text-[var(--ink-3)]">
                          Line {lineNumberOf(draft, issue.lineId)} ·{' '}
                        </span>
                      )}
                      {issue.message}
                      {issue.enforcedBy && (
                        <span className="bb-mono block pt-0.5 text-[10.5px] text-[var(--ink-3)]">
                          {issue.enforcedBy}
                        </span>
                      )}
                    </span>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader
              title="GL effect"
              subtitle={`What reaches voucher_lines${summary.contraSplit ? ' — contra split per line' : ''}`}
            />
            {summary.glLines.length === 0 ? (
              <CardBody>
                <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                  Nothing to post yet — enter an amount.
                </p>
              </CardBody>
            ) : (
              <div className="flex flex-col">
                {summary.glLines.map((line) => (
                  <div
                    key={line.lineNo}
                    className="flex items-center justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 last:border-b-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px] text-[var(--ink)]">
                        {accountLabel(line.accountCode) || '(no account)'}
                      </span>
                      <span className="block truncate text-[11px] text-[var(--ink-3)]">
                        {line.side === 'Dr' ? 'Debit' : 'Credit'}
                        {line.generated ? ' · generated' : ''}
                        {line.partyCode ? ` · ${partyName(line.partyCode)}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <Money value={line.amountBase} className="block text-[12px]" />
                      {foreign && (
                        <span className="bb-num block text-[10.5px] text-[var(--ink-3)]">
                          {formatMoney(line.amountTxn, { currency })} {currency}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        {company()?.reportFooter ??
          `System-generated from ${company()?.name ?? 'biovet'} Accounting.`}{' '}
        Figures in {baseCurrency} unless stated otherwise.
      </p>
    </>
  );
}

type Busy = 'draft' | 'submit' | 'post' | 'delete' | 'load' | 'bulk' | null;


function BulkPanel({
  config,
  type,
  bulk,
  bulkFile,
  result,
  busy,
  disabled,
  fileInput,
  postable,
  blocked,
  onFile,
  onUpload,
  onClear,
}: {
  config: VoucherTypeConfig;
  type: VoucherTypeCode;
  bulk: BulkParse | null;
  bulkFile: string;
  result: BulkResult | null;
  busy: boolean;
  disabled: boolean;
  fileInput: React.RefObject<HTMLInputElement | null>;
  postable: number;
  blocked: number;
  onFile: (file: File | undefined) => void;
  onUpload: (status: SaveStatus) => void;
  onClear: () => void;
}) {
  return (
    <Card flush data-noprint="1">
      <CardHeader
        title="Bulk upload"
        subtitle={`One row per line, grouped by Voucher ref. CSV or .xlsx — every voucher is validated here before any of them is sent.`}
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => downloadText(`${type}-template.csv`, templateCsv(type, config))}
          >
            Download template
          </Button>
        }
      />

      <Toolbar>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="text-[12px] text-[var(--ink-2)] file:mr-2 file:cursor-pointer file:rounded-[var(--radius)] file:border file:border-[var(--line-2)] file:bg-[var(--surface-2)] file:px-2.5 file:py-1 file:text-[12px] file:text-[var(--ink)]"
          disabled={disabled}
          onChange={(e) => onFile(e.target.files?.[0])}
          aria-label={`Upload ${config.label} file`}
        />
        {bulkFile && (
          <>
            <span className="text-[11.5px] text-[var(--ink-3)]">{bulkFile}</span>
            <Button size="sm" variant="ghost" onClick={onClear} disabled={disabled}>
              Clear
            </Button>
          </>
        )}
        <div className="flex-1" />
        {bulk && (
          <>
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              disabled={disabled || bulk.vouchers.length === 0}
              onClick={() => onUpload('DRAFT')}
            >
              Save {bulk.vouchers.length} as drafts
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={disabled || postable === 0}
              onClick={() => onUpload('POSTED')}
            >
              Post {postable}
            </Button>
          </>
        )}
      </Toolbar>

      {bulk && (
        <div className="flex flex-col">
          {bulk.issues.length > 0 && (
            <div className="border-b border-[var(--line-2)] px-[var(--pad)] py-2">
              {bulk.issues.map((issue, i) => (
                <p key={i} className="m-0 text-[11.5px] text-[var(--ink-3)]">
                  Row {issue.row} · {issue.message}
                </p>
              ))}
            </div>
          )}

          {bulk.vouchers.length === 0 ? (
            <CardBody>
              <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                No vouchers found in that file. Check that the header row names the columns — the
                template has the ones this voucher type expects.
              </p>
            </CardBody>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              {bulk.vouchers.map((voucher) => {
                const errors = voucher.validation.errors;
                return (
                  <div
                    key={voucher.reference}
                    className="flex items-start justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 last:border-b-0"
                  >
                    <span className="min-w-0">
                      <span className="block text-[12px] text-[var(--ink)]">
                        <span className="bb-mono">{voucher.reference}</span>
                        <span className="text-[var(--ink-3)]">
                          {' '}
                          · row {voucher.row} · {formatDate(voucher.draft.voucherDate)} ·{' '}
                          {voucher.summary.glLines.length} lines
                        </span>
                      </span>
                      {errors.length > 0 ? (
                        errors.slice(0, 3).map((issue, i) => (
                          <span key={i} className="block text-[11px] text-[var(--debit)]">
                            {issue.message}
                          </span>
                        ))
                      ) : (
                        <span className="block truncate text-[11px] text-[var(--ink-3)]">
                          {voucher.draft.narration || '(no narration)'}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Money value={voucher.summary.totalDebitBase} className="text-[12px]" />
                      {voucher.validation.canPost ? (
                        <Badge tone="ok" dot>
                          Postable
                        </Badge>
                      ) : (
                        <Badge tone="bad">{errors.length}</Badge>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {blocked > 0 && (
            <CardFooter>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                {blocked} of {bulk.vouchers.length} cannot post yet. Posting sends only the{' '}
                {postable} that can — fix the rest and upload them again.
              </span>
            </CardFooter>
          )}
        </div>
      )}

      {result && (
        <div className="border-t border-[var(--line-2)]">
          <div className="px-[var(--pad)] py-2">
            <Callout
              tone={result.failed === 0 ? 'success' : 'warn'}
              title={`${result.succeeded} of ${result.total} written${result.failed > 0 ? `, ${result.failed} refused` : ''}`}
            >
              {result.failed === 0
                ? 'Every voucher in the file is in the ledger. The register above has them.'
                : 'Each voucher was written in its own transaction, so the ones that succeeded are committed and the ones below changed nothing.'}
            </Callout>
          </div>
          {result.results
            .filter((row) => !row.ok)
            .map((row) => (
              <p
                key={row.row}
                className="m-0 border-t border-[var(--line-2)] px-[var(--pad)] py-1.5 text-[11.5px] text-[var(--ink-2)]"
              >
                <span className="bb-mono">{row.reference}</span> · {row.error}
              </p>
            ))}
        </div>
      )}
    </Card>
  );
}


function stamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: companyTimezone(),
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(at);
}

interface PrintLine {
  label: string;
  party: string;
  narration: string;
  debit: string;
  credit: string;
}

function printVoucher({
  config,
  draft,
  summary,
  record,
  currency,
  baseCurrency,
  fxRate,
}: {
  config: VoucherTypeConfig;
  draft: VoucherDraft;
  summary: ReturnType<typeof summarise>;
  record: VoucherDetail | null;
  currency: Currency;
  baseCurrency: Currency;
  fxRate: string;
}) {
  const co = company();
  const foreign = currency !== baseCurrency;

  const printed: PrintLine[] = record
    ? record.lines.map((line) => ({
        label: line.accountName ? `${line.accountCode} · ${line.accountName}` : line.accountCode,
        party: line.partyName ?? '',
        narration: line.narration ?? '',
        debit: line.side === 'Dr' ? line.amount : '',
        credit: line.side === 'Cr' ? line.amount : '',
      }))
    : summary.glLines.map((line) => ({
        label: accountLabel(line.accountCode) || line.accountCode,
        party: line.partyCode ? partyName(line.partyCode) : '',
        narration: line.narration,
        debit: line.side === 'Dr' ? line.amountTxn : '',
        credit: line.side === 'Cr' ? line.amountTxn : '',
      }));

  const row = (line: PrintLine) => `
    <tr>
      <td>${esc(line.label)}${line.party ? `<span class="party"> · ${esc(line.party)}</span>` : ''}</td>
      <td>${esc(line.narration)}</td>
      ${foreign ? `<td class="num">${esc(fxRate)}</td>` : ''}
      <td class="num">${line.debit ? formatMoney(line.debit, { currency }) : ''}</td>
      <td class="num">${line.credit ? formatMoney(line.credit, { currency }) : ''}</td>
    </tr>`;

  const doc = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(draft.voucherNo ?? config.label)}</title>
<style>
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 8px; text-transform: uppercase; letter-spacing: .08em; }
  .meta { display: flex; gap: 28px; font-size: 11.5px; padding-bottom: 10px; }
  .meta b { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #999; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { border-bottom: 1.2px solid #000; font-size: 10.5px; text-transform: uppercase;
       letter-spacing: .06em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td { font-weight: 600; border-top: 1.2px solid #000; border-bottom: 0; }
  .party { color: #555; }
  .sigs { display: flex; gap: 60px; padding-top: 56px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
</style></head>
<body>
  ${letterhead()}

  <h1>${esc(config.label)}</h1>
  <div class="meta">
    <span><b>Voucher no.</b> ${esc(draft.voucherNo ?? '—')}</span>
    <span><b>Date</b> ${esc(formatDate(draft.voucherDate))}</span>
    <span><b>Currency</b> ${esc(currency)}${foreign ? ` @ ${esc(fxRate)}` : ''}</span>
  </div>

  <table>
    <thead>
      <tr>
        <th>Head of account</th>
        <th>Narration</th>
        ${foreign ? '<th class="num">Rate</th>' : ''}
        <th class="num">Debit</th>
        <th class="num">Credit</th>
      </tr>
    </thead>
    <tbody>${printed.map(row).join('')}</tbody>
    <tfoot>
      <tr>
        <td>Total</td>
        <td>${draft.narration ? esc(draft.narration) : ''}</td>
        ${foreign ? '<td></td>' : ''}
        <td class="num">${formatMoney(summary.totalDebitTxn, { currency })}</td>
        <td class="num">${formatMoney(summary.totalCreditTxn, { currency })}</td>
      </tr>
      ${
        foreign
          ? `<tr><td colspan="${3}">Base equivalent · ${esc(baseCurrency)}</td>
             <td class="num">${formatMoney(summary.totalDebitBase)}</td>
             <td class="num">${formatMoney(summary.totalCreditBase)}</td></tr>`
          : ''
      }
    </tfoot>
  </table>

  <div class="sigs">
    <div class="sig">${esc(record?.postedByName ?? record?.createdByName ?? '')}<div class="muted">Prepared by</div></div>
    <div class="sig">&nbsp;<div class="muted">Manager signature</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    ${record?.postedAt ? `Posted ${esc(stamp(record.postedAt))}.` : ''}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(doc);
}


function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) return cause.full;
  if (cause instanceof Error) return cause.message;
  return String(cause);
}

function LineBalance({
  accountCode,
  balanceBase,
  baseCurrency,
}: {
  accountCode: string;
  balanceBase: string | undefined;
  baseCurrency: Currency;
}) {
  const account = accountCode ? findAccount(accountCode) : undefined;

  if (!account) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">—</span>;
  }
  if (balanceBase === undefined) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">…</span>;
  }
  if (cmpDecimal(balanceBase || '0', '0') === 0) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">nil</span>;
  }
  return <Balance value={balanceBase} currency={baseCurrency} className="text-[11.5px]" />;
}

function LockedSide({ side }: { side: 'Dr' | 'Cr' }) {
  return (
    <span className="ml-1 inline-flex items-center gap-1 rounded-[var(--radius-pill)] bg-[var(--line-2)] px-1.5 py-[1px] text-[10px] font-medium tracking-normal text-[var(--ink-2)] normal-case">
      <svg width="8" height="9" viewBox="0 0 10 11" fill="none" aria-hidden="true">
        <path
          d="M2.5 4.5V3a2.5 2.5 0 0 1 5 0v1.5"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <rect x="1" y="4.5" width="8" height="6" rx="1.4" fill="currentColor" opacity="0.75" />
      </svg>
      {side}
    </span>
  );
}

function totalRowLabel(config: VoucherTypeConfig, draft: VoucherDraft, currency: Currency): string {
  if (config.shape === 'JOURNAL') return `Voucher total · ${currency}`;
  const account = draft.contraAccountCode
    ? accountLabel(draft.contraAccountCode)
    : 'no account selected';
  return `${account} · ${config.contra?.side ?? ''}`;
}

function headerSummary(draft: VoucherDraft, config: VoucherTypeConfig, branchName: string): string {
  const parts = [formatDate(draft.voucherDate)];

  if (config.contra) {
    parts.push(
      draft.contraAccountCode
        ? accountLabel(draft.contraAccountCode)
        : `no ${config.contra.kind.toLowerCase()} account`,
    );
  }

  parts.push(branchName);

  const narration = draft.narration.trim();
  if (narration) parts.push(narration.length > 48 ? `${narration.slice(0, 48)}…` : narration);

  return parts.join(' · ');
}

function fieldError(issues: Issue[], field: string): string | undefined {
  return issues.find((i) => i.field === field && i.severity === 'error')?.message;
}

function lineNumberOf(draft: VoucherDraft, lineId: string): number {
  return draft.lines.findIndex((line) => line.id === lineId) + 1;
}

function partyName(code: string): string {
  return findParty(code)?.name ?? code;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 9 6"
      fill="none"
      aria-hidden="true"
      className="transition-transform duration-[var(--dur-fast)]"
      style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
    >
      <path
        d="M1 1.5L4.5 5L8 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function matchesNumber(voucherNo: string, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return false;
  const hay = voucherNo.toLowerCase();
  if (hay.includes(needle)) return true;
  if (/^\d+$/.test(needle)) {
    const tail = hay.split('-').pop() ?? '';
    return Number(tail) === Number(needle);
  }
  return false;
}

function keepsSubLedger(accountCode: string): boolean {
  return accountCode ? Boolean(findAccount(accountCode)?.requiresParty) : false;
}

function lineColumns(
  config: VoucherTypeConfig,
  currency: Currency,
  baseCurrency: Currency,
): EntryColumn[] {
  const amounts: EntryColumn[] =
    config.shape === 'JOURNAL'
      ? [
          { key: 'debit', label: `Debit ${currency}`, width: '150px', numeric: true },
          { key: 'credit', label: `Credit ${currency}`, width: '150px', numeric: true },
        ]
      : [
          {
            key: 'amount',
            label: `${config.amountLabel} ${currency}`,
            width: '164px',
            numeric: true,
          },
        ];

  return [
    { key: 'no', label: '#', width: '40px' },
    { key: 'account', label: 'Account', required: true },
    { key: 'subAccount', label: 'Sub account', width: '200px' },
    { key: 'narration', label: 'Narration' },
    { key: 'balance', label: `Balance ${baseCurrency}`, width: '160px', numeric: true },
    ...amounts,
    { key: 'actions', label: '', width: '38px', noPrint: true },
  ];
}

function groupOf(account: Account): string {
  switch (account.type) {
    case 'ASSET':
      return '1000 Assets';
    case 'LIABILITY':
      return '2000 Liabilities';
    case 'EQUITY':
      return '3000 Equity';
    case 'INCOME':
      return '4000 Income';
    default:
      return account.code.startsWith('5') ? '5000 Cost of sales' : '6000 Operating expenses';
  }
}

const GROUP_ORDER = [
  '1000 Assets',
  '2000 Liabilities',
  '3000 Equity',
  '4000 Income',
  '5000 Cost of sales',
  '6000 Operating expenses',
];

function accountOptionsFor(accounts: Account[], config: VoucherTypeConfig): ComboboxOption[] {
  if (accounts.length === 0) return [];

  return entryAccounts()
    .map((account) => {
      const subs = subAccountsOf(account.code).length;
      const barred =
        config.shape === 'JOURNAL' &&
        Boolean(account.control) &&
        !account.allowDirectPosting &&
        subs === 0;

      return {
        value: account.code,
        label: `${account.code} · ${account.name}`,
        hint: barred
          ? 'control · not on a JV'
          : subs > 0
            ? `${subs} sub-account${subs === 1 ? '' : 's'}`
            : account.fcyCurrency
              ? account.fcyCurrency
              : undefined,
        group: groupOf(account),
        disabled: barred,
      } satisfies ComboboxOption;
    })
    .sort((a, b) => {
      const byGroup = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
      return byGroup !== 0 ? byGroup : a.value.localeCompare(b.value);
    });
}

function subAccountOptionsFor(parentCode: string): ComboboxOption[] {
  return subAccountsOf(parentCode).map((account) => ({
    value: account.code,
    label: `${account.code} · ${account.name}`,
  }));
}

const PARTY_OPTIONS: ComboboxOption[] = PARTIES.map((party) => ({
  value: party.code,
  label: party.name,
  hint: party.code,
  group:
    party.kind === 'CUSTOMER'
      ? 'Customers'
      : party.kind === 'SUPPLIER'
        ? 'Suppliers'
        : party.kind === 'BROKER_SELLER'
          ? 'Brokerage — Party A'
          : 'Salespersons',
}));
